import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";
import {
  dismissInstallBanner,
  shouldShowInstallBanner,
} from "@/lib/pwaInstall";

/**
 * Banniere subtile sur iPad Safari qui invite a ajouter l'app a l'ecran
 * d'accueil. Maillon final de l'auth perenne :
 *
 *   localStorage (purge ITP 7j) -> IndexedDB (purge ITP plus lente)
 *   -> magic link auto-recovery -> **PWA standalone (jamais purge)**
 *
 * Quand iOS Safari ouvre l'app installee, le storage est considere
 * comme du first-party permanent : ITP ne s'applique plus du tout.
 * L'arbitre reste connecte aussi longtemps que son refresh token
 * Supabase reste valide (1 an par defaut), sans risque ITP.
 *
 * Affichage :
 *   - seulement sur iPad Safari (les autres OS ont d'autres mecanismes) ;
 *   - seulement si NON installee (`navigator.standalone` false) ;
 *   - seulement si non dismissed dans les 30 derniers jours.
 *
 * Dismissable individuellement, re-propose apres 30j (anti-spam mais
 * rappel periodique pour ne pas oublier).
 */
export function InstallBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Eval cote client uniquement, apres montage (sinon SSR hydration
    // mismatch). Petit delai pour eviter le flash si on est en cours
    // de redirect d'auth.
    const t = setTimeout(() => setShow(shouldShowInstallBanner()), 800);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const onDismiss = () => {
    dismissInstallBanner();
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Installer l'application sur l'écran d'accueil"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50"
    >
      <div
        className="rounded-lg p-4 shadow-lg"
        style={{
          background: "var(--arbitre-slate-800, #1f1735)",
          border: "1px solid var(--ai-violet, #7c3aed)",
          color: "var(--arbitre-slate-100, #f5f5fa)",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="rounded-md p-2 shrink-0"
            style={{ background: "rgba(124, 58, 237, 0.15)" }}
          >
            <Share size={18} style={{ color: "var(--ai-violet, #7c3aed)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              Installer GSF Arbitral sur l'iPad
            </div>
            <div
              className="text-xs mt-1"
              style={{ color: "var(--arbitre-slate-300, #cbcbd6)" }}
            >
              Touchez{" "}
              <Share size={11} className="inline mx-0.5 -mt-0.5" /> dans la
              barre Safari, puis « Sur l'écran d'accueil ». L'app reste
              ouverte plus longtemps sans avoir à se reconnecter.
            </div>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Fermer la suggestion d'installation"
            className="shrink-0 rounded-md p-1.5"
            style={{
              color: "var(--arbitre-slate-400, #94939e)",
              background: "transparent",
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
